import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Bell,
  Menu,
  X,
  ChevronDown,
  CalendarDays,
  PartyPopper,
  Landmark,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../../assets/images/logo.png";
import Button from "./Button";
import { useTranslation } from "react-i18next";
import { FaRegUserCircle } from "react-icons/fa";
import { prefetchRoute } from "utils/preloadPublicRoutes";
import AppStoreButtons from "./AppStoreButtons";
import JoinAppModal from "./JoinAppModal";
import { useDispatch } from "react-redux";
import { onLocaleChange } from "store/slices/themeSlice";

// const NAV_ITEMS = [
//   { label: t("nav.home") },

//   { label: t("nav.about") },

//   {
//     label: t("nav.events"),
//     dropdown: [
//       {
//         label: t("nav.upcomingEvents"),
//         icon: CalendarDays,
//         desc: t("nav.upcomingEventsDesc"),
//       },
//       {
//         label: t("nav.festivals"),
//         icon: PartyPopper,
//         desc: t("nav.festivalsDesc"),
//       },
//       {
//         label: t("nav.templeEvents"),
//         icon: Landmark,
//         desc: t("nav.templeEventsDesc"),
//       },
//     ],
//   },

//   {
//     label: t("nav.bloodDonation"),
//   },

//   {
//     label: t("nav.donation"),
//   },

//   {
//     label: t("nav.contact"),
//   },
// ];
function NavLink({ item, isActive }) {
  const hasDropdown = Boolean(item.dropdown);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const showUnderline = isActive || open;

  return (
    <div
      className="relative mx-1"
      onMouseEnter={hasDropdown ? handleEnter : undefined}
      onMouseLeave={hasDropdown ? handleLeave : undefined}
    >
      <Link
        to={item.to || "#"}
        onMouseEnter={() => prefetchRoute(item.to)}
        onFocus={() => prefetchRoute(item.to)}
        className={`relative flex items-center gap-1 rounded-full border-0 bg-transparent px-3.5 py-2 text-[13.5px] font-medium tracking-wide transition-colors duration-300 ${
          showUnderline
            ? "text-[var(--text-color)]"
            : "text-[var(--light-color)] hover:text-[var(--text-color)]"
        }`}
      >
        {item.label}
        {hasDropdown && (
          <ChevronDown
            size={14}
            className={`text-[var(--heading-color)] transition-transform duration-300 ${
              open ? "-rotate-180 text-[var(--text-color)]" : ""
            }`}
          />
        )}
        <span
          className={`absolute -bottom-0.5 left-3.5 right-3.5 h-[2px] origin-center rounded-full bg-gradient-to-r from-[var(--primary-color)] to-[var(--text-color)] transition-transform duration-300 ${
            showUnderline ? "scale-x-100" : "scale-x-0"
          }`}
        />
      </Link>
    </div>
  );
}

function isNavActive(pathname, to) {
  if (!to) return false;
  const path = pathname.replace(/\/$/, "") || "/";
  if (to === "/") return path === "/";
  return path === to || path.startsWith(`${to}/`);
}

export default function Header() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);

  const lang = i18n.language.startsWith("hi") ? "HI" : "EN";
  const langKey = lang === "HI" ? "hi" : "en";

  const switchLanguage = () => {
    const newLang = lang === "EN" ? "hi" : "en";
    i18n.changeLanguage(newLang);
    dispatch(onLocaleChange(newLang));
  };

  const openJoinModal = (e) => {
    if (e) e.preventDefault();
    setMobileOpen(false);
    setJoinModalOpen(true);
  };
  const NAV_ITEMS = [
    {
      label: t("nav.home"),
      to: "/",
    },
    {
      label: t("nav.about"),
      to: "/about",
    },
    {
      label: t("nav.events"),
      to: "/events",
      // dropdown: [
      //   {
      //     label: t("nav.upcomingEvents"),
      //     to: "/events/upcoming",
      //     icon: CalendarDays,
      //     desc: t("nav.upcomingEventsDesc"),
      //   },
      //   {
      //     label: t("nav.festivals"),
      //     to: "/events/festivals",
      //     icon: PartyPopper,
      //     desc: t("nav.festivalsDesc"),
      //   },
      //   {
      //     label: t("nav.templeEvents"),
      //     to: "/events/temples",
      //     icon: Landmark,
      //     desc: t("nav.templeEventsDesc"),
      //   },
      // ],
    },

    {
      label: t("nav.sanatan"),
      to: "/sanatan",
    },
    {
      label: t("nav.donation"),
      to: "/donation",
    },
    {
      label: t("nav.emergencyHelp"),
      to: "/emergency-help",
    },
    {
      label: t("nav.contact"),
      to: "/contact",
    },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-5 left-1/2 z-[999] w-[95%] max-w-[1320px] -translate-x-1/2
  rounded-2xl border border-[#F4E9DB] backdrop-blur-[18px]
  transition-all duration-500 lg:rounded-full
  ${mobileOpen ? "overflow-hidden" : "overflow-visible"}
  ${
    scrolled
      ? "bg-white/90 shadow-[0_20px_60px_rgba(0,0,0,0.10)]"
      : "bg-white/75 shadow-[0_10px_35px_rgba(0,0,0,0.05)]"
  }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:h-20 sm:gap-3 sm:px-6 lg:px-8">
        {/* Left — Brand */}
        <Link
          to="/"
          className="flex min-w-0 max-w-[calc(100%-118px)] items-center gap-1.5 sm:gap-2 xl:max-w-none xl:shrink-0"
        >
          <img
            src={Logo}
            alt={t("brand.title")}
            className="h-12 w-12 shrink-0 object-contain sm:h-[70px] sm:w-[70px]"
          />
          <AnimatePresence mode="wait">
            <motion.div
              key={langKey}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="flex min-w-0 flex-col justify-center overflow-visible leading-snug"
            >
              <p className="whitespace-nowrap text-[15px] font-bold tracking-tight text-[var(--secondary-color)] sm:text-[17px]">
                {t("brand.title")}
              </p>
              <p className="mt-0.5 whitespace-nowrap text-[10px] font-semibold leading-normal tracking-wide text-[var(--heading-color)] sm:text-[11px]">
                {t("brand.subtitle")}
              </p>
            </motion.div>
          </AnimatePresence>
        </Link>

        {/* Center — Nav (desktop) */}
        <nav className="hidden items-center xl:flex">
          <AnimatePresence mode="wait">
            <motion.div
              key={`nav-${langKey}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="flex items-center"
            >
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  item={item}
                  isActive={isNavActive(pathname, item.to)}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </nav>

        {/* Right — Actions (desktop) */}
        <div className="relative z-[1100] hidden flex-shrink-0 items-center gap-2 overflow-visible xl:flex">
          {/* Search */}
          <div className="flex items-center">
            {/* <AnimatePresence>
              {searchOpen && (
                <motion.input
                  key="search-input"
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: 170, opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  autoFocus
                  type="text"
                  placeholder={t("search")}
                  className="mr-1 rounded-full border border-[var(--bg-color)] bg-white/70 px-4 py-2 text-[13px] text-[var(--light-color)] outline-none placeholder:text-[var(--light-color)]/50 focus:border-[var(--primary-color)]"
                />
              )}
            </AnimatePresence> */}

            {/* <motion.button
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
              onClick={() => setSearchOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-full text-[var(--secondary-color)] transition-colors hover:bg-[var(--bg-color)]"
              aria-label="Search"
            >
              <Search size={18} />
            </motion.button> */}
          </div>

          {/* Notifications */}
          {/* <motion.button
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.94 }}
            className="relative flex h-10 w-10 items-center justify-center rounded-full text-[var(--secondary-color)] transition-colors hover:bg-[var(--bg-color)]"
            aria-label="Notifications"
          >
            <Bell size={18} />
            <motion.span
              animate={{ scale: [1, 1.25, 1] }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[var(--text-color)] ring-2 ring-white"
            />
          </motion.button> */}

          {/* <Link
  to="/profile"
  className="group relative"
>
  <img
    src="https://mironcoder-hotash-react.netlify.app/images/avatar/01.webp"
    alt="Profile"
    className="h-11 w-11 rounded-full border-solid border-2 p-0.5 !border-saffron object-cover transition duration-300  "
  />
</Link> */}

          {/* Download App */}
          <AppStoreButtons size="sm" tooltipPlacement="top" />

          {/* Language switch */}
          <button
            onClick={switchLanguage}
            className="relative flex h-9 w-[92px] items-center rounded-full bg-[var(--bg-color)] p-1 text-[11.5px] font-semibold"
          >
            <motion.span
              layout
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className={`absolute h-7 w-[44px] rounded-full bg-white shadow-sm ${
                lang === "EN" ? "left-1" : "left-[46px]"
              }`}
            />

            <span
              className={`z-10 flex-1 text-center transition-colors ${
                lang === "EN"
                  ? "text-[var(--text-color)]"
                  : "text-[var(--light-color)]/50"
              }`}
            >
              EN
            </span>

            <span
              className={`z-10 flex-1 text-center transition-colors ${
                lang === "HI"
                  ? "text-[var(--text-color)]"
                  : "text-[var(--light-color)]/50"
              }`}
            >
              हिन्दी
            </span>
          </button>

          {/* Join Community */}
          <Button
            to="#"
            variant="maroon"
            onClick={openJoinModal}
            whileHover={{
              scale: 1.035,
              boxShadow: "0 14px 28px -8px rgba(241,70,1,0.6)",
            }}
            whileTap={{ scale: 0.97 }}
            className="!w-[220px] !min-w-[220px] !max-w-[220px]"
            textClassName="!justify-center"
          >
            {t("joinCommunity")}
          </Button>
        </div>

        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="absolute left-0 top-full w-full border-t border-gray-200   z-50"
            >
              <div className="max-w-7xl mx-auto px-4 py-3 flex justify-end">
                <input
                  autoFocus
                  type="text"
                  placeholder={t("search")}
                  className="w-[320px] rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm outline-none transition-all duration-300  focus:border-[var(--primary-color)]"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile — language (left) + hamburger */}
        <div className="flex shrink-0 items-center gap-1.5 xl:hidden">
          <button
            onClick={switchLanguage}
            className="relative flex h-9 w-[92px] items-center rounded-full bg-[var(--bg-color)] p-1 text-[11.5px] font-semibold"
          >
            <motion.span
              layout
              transition={{ type: "spring", stiffness: 400, damping: 32 }}
              className={`absolute h-7 w-[44px] rounded-full bg-white shadow-sm ${
                lang === "EN" ? "left-1" : "left-[46px]"
              }`}
            />
            <span
              className={`z-10 flex-1 text-center transition-colors ${
                lang === "EN"
                  ? "text-[var(--text-color)]"
                  : "text-[var(--light-color)]/50"
              }`}
            >
              EN
            </span>
            <span
              className={`z-10 flex-1 text-center transition-colors ${
                lang === "HI"
                  ? "text-[var(--text-color)]"
                  : "text-[var(--light-color)]/50"
              }`}
            >
              हिन्दी
            </span>
          </button>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[var(--secondary-color)]"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-black/5 bg-white/[.97] backdrop-blur-xl xl:hidden"
          >
            <div className="flex max-h-[min(70vh,calc(100dvh-8rem))] flex-col gap-1 overflow-y-auto overscroll-contain px-6 py-4 [-webkit-overflow-scrolling:touch]">
              {NAV_ITEMS.map((item, i) => {
                const active = isNavActive(pathname, item.to);
                return (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <Link
                      to={item.to || "#"}
                      onClick={() => setMobileOpen(false)}
                      onMouseEnter={() => prefetchRoute(item.to)}
                      onTouchStart={() => prefetchRoute(item.to)}
                      className={`block rounded-xl px-3 py-3 text-[15px] font-medium transition-colors ${
                        active
                          ? "bg-[var(--bg-color)] text-[var(--text-color)]"
                          : "text-[var(--light-color)] hover:bg-[var(--bg-color)] hover:text-[var(--text-color)]"
                      }`}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                );
              })}

              <div className="mt-3 flex items-center justify-center gap-3 border-t border-border/60 px-3 pt-4">
                <AppStoreButtons size="sm" />
              </div>

              <div className="mt-3 flex items-center gap-3 px-3">
                <button
                  type="button"
                  onClick={openJoinModal}
                  className="flex-1 rounded-full bg-[var(--secondary-color)] py-2.5 text-[13px] font-semibold text-white shadow-md"
                >
                  {t("joinCommunity")}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <JoinAppModal
        open={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
      />
    </header>
  );
}
